'use client';
import React, { useState, useMemo } from 'react';
import AppLayout from '@/components/AppLayout';
import { Banknote, CheckCircle, AlertTriangle, Search, Download, X, ChevronDown, ChevronUp, Eye, RefreshCw, Clock, XCircle, CreditCard, Building2, MapPin, Hash, DollarSign, FileText, BarChart2, ArrowUpRight, CheckSquare, Square, AlertCircle, Layers, Phone, Printer, ShieldCheck, RotateCcw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, AreaChart, Area, Line, CartesianGrid } from 'recharts';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

type PaymentStatus = 'settled' | 'pending' | 'failed' | 'reversed' | 'disputed' | 'partial' | 'exempt';
type PaymentMethod = 'Bank Transfer' | 'Orange Money' | 'MTN Money' | 'POS / Card' | 'USSD' | 'Cash' | 'Cheque' | 'Online Portal';
type ReconciliationStatus = 'reconciled' | 'unreconciled' | 'in_review' | 'exception' | 'pending_bank';
type TaxType = 'Property Tax' | 'Income Tax' | 'Business Tax' | 'Customs Duty' | 'Excise Tax' | 'Capital Gains';

interface Payment {
  id: string;
  billId: string;
  taxpayerId: string;
  taxpayerName: string;
  taxType: TaxType;
  county: string;
  district: string;
  amount: number;
  currency: string;
  paymentMethod: PaymentMethod;
  channel: string;
  referenceNo: string;
  bankRef: string;
  paymentDate: string;
  valueDate: string;
  status: PaymentStatus;
  reconciliationStatus: ReconciliationStatus;
  reconciledBy: string;
  reconciledDate: string;
  collectionOfficer: string;
  officeLocation: string;
  notes: string;
  installmentNo: number | null;
  totalInstallments: number | null;
  penaltyIncluded: number;
  discountApplied: number;
}

interface ReconciliationItem {
  id: string;
  paymentId: string;
  billId: string;
  taxpayerName: string;
  amount: number;
  bankStatement: string;
  systemRecord: string;
  variance: number;
  status: ReconciliationStatus;
  assignedTo: string;
  flagReason: string;
  createdDate: string;
  resolvedDate: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const COUNTIES = ['All Counties', 'Montserrado', 'Grand Bassa', 'Nimba', 'Bong', 'Lofa', 'Margibi', 'Maryland', 'Grand Gedeh', 'Sinoe', 'Grand Cape Mount', 'Gbarpolu', 'River Gee', 'Grand Kru', 'River Cess', 'Rivercess'];

const PAYMENTS: Payment[] = [
  { id: 'PAY-2025-001847', billId: 'BILL-S6-00291', taxpayerId: 'TIN-LR-0041823', taxpayerName: 'Kollie Trading Co. Ltd.', taxType: 'Property Tax', county: 'Montserrado', district: 'Greater Monrovia', amount: 84200, currency: 'USD', paymentMethod: 'Bank Transfer', channel: 'Ecobank Liberia', referenceNo: 'ECO-2025-884712', bankRef: 'BNK-TXN-99201', paymentDate: '2025-05-27', valueDate: '2025-05-27', status: 'settled', reconciliationStatus: 'reconciled', reconciledBy: 'J. Kollie', reconciledDate: '2025-05-28', collectionOfficer: 'A. Freeman', officeLocation: 'Monrovia HQ', notes: '', installmentNo: null, totalInstallments: null, penaltyIncluded: 0, discountApplied: 2100 },
  { id: 'PAY-2025-001203', billId: 'BILL-S6-00187', taxpayerId: 'TIN-LR-0038201', taxpayerName: 'Gedeh Commerce Ltd.', taxType: 'Business Tax', county: 'Grand Gedeh', district: 'Zwedru', amount: 18300, currency: 'USD', paymentMethod: 'Orange Money', channel: 'Orange Liberia', referenceNo: 'OM-2025-441203', bankRef: '', paymentDate: '2025-05-27', valueDate: '2025-05-28', status: 'settled', reconciliationStatus: 'reconciled', reconciledBy: 'M. Weah', reconciledDate: '2025-05-29', collectionOfficer: 'B. Kollie', officeLocation: 'Zwedru Office', notes: '', installmentNo: 1, totalInstallments: 2, penaltyIncluded: 300, discountApplied: 0 },
  { id: 'PAY-2025-000877', billId: 'BILL-S6-00142', taxpayerId: 'TIN-LR-0029441', taxpayerName: 'Maryland Fisheries Ltd.', taxType: 'Property Tax', county: 'Maryland', district: 'Harper', amount: 22800, currency: 'USD', paymentMethod: 'Bank Transfer', channel: 'LBDI Bank', referenceNo: 'LBDI-2025-330091', bankRef: 'BNK-TXN-88120', paymentDate: '2025-05-26', valueDate: '2025-05-26', status: 'settled', reconciliationStatus: 'reconciled', reconciledBy: 'S. Toe', reconciledDate: '2025-05-27', collectionOfficer: 'C. Nimely', officeLocation: 'Harper Office', notes: '', installmentNo: null, totalInstallments: null, penaltyIncluded: 0, discountApplied: 0 },
  { id: 'PAY-2025-000412', billId: 'BILL-S6-00098', taxpayerId: 'TIN-LR-0011204', taxpayerName: 'Fatima Kamara', taxType: 'Property Tax', county: 'Lofa', district: 'Voinjama', amount: 4800, currency: 'USD', paymentMethod: 'MTN Money', channel: 'MTN Liberia', referenceNo: 'MTN-2025-220041', bankRef: '', paymentDate: '2025-05-26', valueDate: '2025-05-27', status: 'settled', reconciliationStatus: 'reconciled', reconciledBy: 'A. Konneh', reconciledDate: '2025-05-28', collectionOfficer: 'D. Fofana', officeLocation: 'Voinjama Office', notes: '', installmentNo: 2, totalInstallments: 4, penaltyIncluded: 0, discountApplied: 0 },
  { id: 'PAY-2025-000934', billId: 'BILL-S6-00231', taxpayerId: 'TIN-LR-0052001', taxpayerName: 'LPA Holdings Ltd.', taxType: 'Property Tax', county: 'Grand Bassa', district: 'Buchanan', amount: 156400, currency: 'USD', paymentMethod: 'Bank Transfer', channel: 'CBL Settlement', referenceNo: 'CBL-2025-991002', bankRef: 'BNK-TXN-77341', paymentDate: '2025-05-25', valueDate: '2025-05-26', status: 'pending', reconciliationStatus: 'pending_bank', reconciledBy: '', reconciledDate: '', collectionOfficer: 'E. Jallah', officeLocation: 'Buchanan Office', notes: 'Awaiting CBL confirmation', installmentNo: null, totalInstallments: null, penaltyIncluded: 0, discountApplied: 0 },
  { id: 'PAY-2025-000741', billId: 'BILL-S6-00178', taxpayerId: 'TIN-LR-0044112', taxpayerName: 'Margibi Industrial Corp.', taxType: 'Business Tax', county: 'Margibi', district: 'Kakata', amount: 31700, currency: 'USD', paymentMethod: 'POS / Card', channel: 'Visa/Mastercard', referenceNo: 'POS-2025-110441', bankRef: 'BNK-TXN-66201', paymentDate: '2025-05-24', valueDate: '2025-05-24', status: 'failed', reconciliationStatus: 'exception', reconciledBy: '', reconciledDate: '', collectionOfficer: 'F. Mulbah', officeLocation: 'Kakata Office', notes: 'Card declined — insufficient funds', installmentNo: null, totalInstallments: null, penaltyIncluded: 0, discountApplied: 0 },
  { id: 'PAY-2025-000612', billId: 'BILL-S6-00155', taxpayerId: 'TIN-LR-0033901', taxpayerName: 'Nimba Agri Cooperative', taxType: 'Property Tax', county: 'Nimba', district: 'Sanniquellie', amount: 9400, currency: 'USD', paymentMethod: 'USSD', channel: 'Orange Money USSD', referenceNo: 'USSD-2025-880012', bankRef: '', paymentDate: '2025-05-23', valueDate: '2025-05-24', status: 'settled', reconciliationStatus: 'reconciled', reconciledBy: 'J. Kollie', reconciledDate: '2025-05-25', collectionOfficer: 'G. Toe', officeLocation: 'Sanniquellie Office', notes: '', installmentNo: 3, totalInstallments: 4, penaltyIncluded: 200, discountApplied: 0 },
  { id: 'PAY-2025-000501', billId: 'BILL-S6-00121', taxpayerId: 'TIN-LR-0027801', taxpayerName: 'Bong Mining Corp.', taxType: 'Income Tax', county: 'Bong', district: 'Gbarnga', amount: 248000, currency: 'USD', paymentMethod: 'Bank Transfer', channel: 'Ecobank Liberia', referenceNo: 'ECO-2025-771201', bankRef: 'BNK-TXN-55001', paymentDate: '2025-05-22', valueDate: '2025-05-22', status: 'settled', reconciliationStatus: 'reconciled', reconciledBy: 'M. Weah', reconciledDate: '2025-05-23', collectionOfficer: 'H. Kpaan', officeLocation: 'Gbarnga Office', notes: '', installmentNo: null, totalInstallments: null, penaltyIncluded: 0, discountApplied: 12400 },
  { id: 'PAY-2025-000388', billId: 'BILL-S6-00099', taxpayerId: 'TIN-LR-0019201', taxpayerName: 'Sinoe Rubber Plantation', taxType: 'Property Tax', county: 'Sinoe', district: 'Greenville', amount: 67300, currency: 'USD', paymentMethod: 'Cheque', channel: 'Physical Cheque', referenceNo: 'CHQ-2025-440021', bankRef: 'BNK-TXN-44100', paymentDate: '2025-05-21', valueDate: '2025-05-23', status: 'settled', reconciliationStatus: 'in_review', reconciledBy: '', reconciledDate: '', collectionOfficer: 'I. Blamo', officeLocation: 'Greenville Office', notes: 'Cheque clearing in progress', installmentNo: null, totalInstallments: null, penaltyIncluded: 3300, discountApplied: 0 },
  { id: 'PAY-2025-000271', billId: 'BILL-S6-00077', taxpayerId: 'TIN-LR-0008801', taxpayerName: 'Cape Mount Timber Ltd.', taxType: 'Property Tax', county: 'Grand Cape Mount', district: 'Robertsport', amount: 14200, currency: 'USD', paymentMethod: 'Online Portal', channel: 'LRA Web Portal', referenceNo: 'WEB-2025-330091', bankRef: 'BNK-TXN-33001', paymentDate: '2025-05-20', valueDate: '2025-05-20', status: 'settled', reconciliationStatus: 'reconciled', reconciledBy: 'S. Toe', reconciledDate: '2025-05-21', collectionOfficer: 'J. Kpoto', officeLocation: 'Robertsport Office', notes: '', installmentNo: null, totalInstallments: null, penaltyIncluded: 0, discountApplied: 0 },
  { id: 'PAY-2025-000199', billId: 'BILL-S6-00061', taxpayerId: 'TIN-LR-0005501', taxpayerName: 'Rivercess Palm Oil Co.', taxType: 'Business Tax', county: 'Rivercess', district: 'Cestos City', amount: 8900, currency: 'USD', paymentMethod: 'Cash', channel: 'LRA Cash Counter', referenceNo: 'CASH-2025-220041', bankRef: '', paymentDate: '2025-05-19', valueDate: '2025-05-19', status: 'settled', reconciliationStatus: 'reconciled', reconciledBy: 'A. Konneh', reconciledDate: '2025-05-19', collectionOfficer: 'K. Sumo', officeLocation: 'Cestos City Office', notes: '', installmentNo: 1, totalInstallments: 2, penaltyIncluded: 0, discountApplied: 0 },
  { id: 'PAY-2025-000142', billId: 'BILL-S6-00048', taxpayerId: 'TIN-LR-0003201', taxpayerName: 'Gbarpolu Dev. Authority', taxType: 'Property Tax', county: 'Gbarpolu', district: 'Bopolu', amount: 5600, currency: 'USD', paymentMethod: 'MTN Money', channel: 'MTN Liberia', referenceNo: 'MTN-2025-110021', bankRef: '', paymentDate: '2025-05-18', valueDate: '2025-05-19', status: 'disputed', reconciliationStatus: 'exception', reconciledBy: '', reconciledDate: '', collectionOfficer: 'L. Kollie', officeLocation: 'Bopolu Office', notes: 'Taxpayer disputes amount — review pending', installmentNo: null, totalInstallments: null, penaltyIncluded: 600, discountApplied: 0 },
  { id: 'PAY-2025-000091', billId: 'BILL-S6-00031', taxpayerId: 'TIN-LR-0001901', taxpayerName: 'River Gee Fisheries', taxType: 'Property Tax', county: 'River Gee', district: 'Fish Town', amount: 3200, currency: 'USD', paymentMethod: 'Orange Money', channel: 'Orange Liberia', referenceNo: 'OM-2025-090041', bankRef: '', paymentDate: '2025-05-17', valueDate: '2025-05-18', status: 'partial', reconciliationStatus: 'unreconciled', reconciledBy: '', reconciledDate: '', collectionOfficer: 'M. Nyahn', officeLocation: 'Fish Town Office', notes: 'Partial payment — balance outstanding', installmentNo: 1, totalInstallments: 3, penaltyIncluded: 0, discountApplied: 0 },
  { id: 'PAY-2025-000044', billId: 'BILL-S6-00018', taxpayerId: 'TIN-LR-0000801', taxpayerName: 'Grand Kru Cocoa Board', taxType: 'Business Tax', county: 'Grand Kru', district: 'Barclayville', amount: 7100, currency: 'USD', paymentMethod: 'Bank Transfer', channel: 'LBDI Bank', referenceNo: 'LBDI-2025-050021', bankRef: 'BNK-TXN-11001', paymentDate: '2025-05-16', valueDate: '2025-05-16', status: 'reversed', reconciliationStatus: 'exception', reconciledBy: '', reconciledDate: '', collectionOfficer: 'N. Gbeh', officeLocation: 'Barclayville Office', notes: 'Payment reversed — duplicate transaction', installmentNo: null, totalInstallments: null, penaltyIncluded: 0, discountApplied: 0 },
  { id: 'PAY-2025-000021', billId: 'BILL-S6-00009', taxpayerId: 'TIN-LR-0000401', taxpayerName: 'Monrovia City Corp.', taxType: 'Property Tax', county: 'Montserrado', district: 'Greater Monrovia', amount: 312000, currency: 'USD', paymentMethod: 'Bank Transfer', channel: 'CBL Settlement', referenceNo: 'CBL-2025-020011', bankRef: 'BNK-TXN-00901', paymentDate: '2025-05-15', valueDate: '2025-05-15', status: 'settled', reconciliationStatus: 'reconciled', reconciledBy: 'J. Kollie', reconciledDate: '2025-05-16', collectionOfficer: 'A. Freeman', officeLocation: 'Monrovia HQ', notes: '', installmentNo: null, totalInstallments: null, penaltyIncluded: 0, discountApplied: 15600 },
];

const RECONCILIATION_ITEMS: ReconciliationItem[] = [
  { id: 'REC-2025-0041', paymentId: 'PAY-2025-000934', billId: 'BILL-S6-00231', taxpayerName: 'LPA Holdings Ltd.', amount: 156400, bankStatement: 'CBL-2025-991002', systemRecord: 'PAY-2025-000934', variance: 0, status: 'pending_bank', assignedTo: 'E. Jallah', flagReason: 'Awaiting CBL bank confirmation', createdDate: '2025-05-25', resolvedDate: '' },
  { id: 'REC-2025-0038', paymentId: 'PAY-2025-000741', billId: 'BILL-S6-00178', taxpayerName: 'Margibi Industrial Corp.', amount: 31700, bankStatement: 'N/A', systemRecord: 'PAY-2025-000741', variance: 31700, status: 'exception', assignedTo: 'F. Mulbah', flagReason: 'Card declined — no bank debit recorded', createdDate: '2025-05-24', resolvedDate: '' },
  { id: 'REC-2025-0031', paymentId: 'PAY-2025-000388', billId: 'BILL-S6-00099', taxpayerName: 'Sinoe Rubber Plantation', amount: 67300, bankStatement: 'CHQ-2025-440021', systemRecord: 'PAY-2025-000388', variance: 0, status: 'in_review', assignedTo: 'I. Blamo', flagReason: 'Cheque clearing — 2-day hold', createdDate: '2025-05-21', resolvedDate: '' },
  { id: 'REC-2025-0028', paymentId: 'PAY-2025-000142', billId: 'BILL-S6-00048', taxpayerName: 'Gbarpolu Dev. Authority', amount: 5600, bankStatement: 'MTN-2025-110021', systemRecord: 'PAY-2025-000142', variance: 600, status: 'exception', assignedTo: 'L. Kollie', flagReason: 'Taxpayer disputes penalty amount of $600', createdDate: '2025-05-18', resolvedDate: '' },
  { id: 'REC-2025-0019', paymentId: 'PAY-2025-000091', billId: 'BILL-S6-00031', taxpayerName: 'River Gee Fisheries', amount: 3200, bankStatement: 'OM-2025-090041', systemRecord: 'PAY-2025-000091', variance: 6400, status: 'unreconciled', assignedTo: 'M. Nyahn', flagReason: 'Partial payment — balance of $6,400 outstanding', createdDate: '2025-05-17', resolvedDate: '' },
  { id: 'REC-2025-0012', paymentId: 'PAY-2025-000044', billId: 'BILL-S6-00018', taxpayerName: 'Grand Kru Cocoa Board', amount: 7100, bankStatement: 'LBDI-2025-050021', systemRecord: 'PAY-2025-000044', variance: -7100, status: 'exception', assignedTo: 'N. Gbeh', flagReason: 'Duplicate transaction — reversal processed', createdDate: '2025-05-16', resolvedDate: '2025-05-17' },
];

// ─── Chart Data ───────────────────────────────────────────────────────────────

const TREND_DATA = [
  { month: 'Dec', collected: 78.1, target: 80 },
  { month: 'Jan', collected: 54.8, target: 75 },
  { month: 'Feb', collected: 71.3, target: 78 },
  { month: 'Mar', collected: 83.7, target: 82 },
  { month: 'Apr', collected: 91.2, target: 85 },
  { month: 'May', collected: 68.5, target: 88 },
];

const COUNTY_DATA = [
  { county: 'Montserrado', amount: 396.2, target: 420 },
  { county: 'Grand Bassa', amount: 156.4, target: 160 },
  { county: 'Bong', amount: 248.0, target: 240 },
  { county: 'Nimba', amount: 9.4, target: 45 },
  { county: 'Lofa', amount: 4.8, target: 30 },
  { county: 'Margibi', amount: 31.7, target: 55 },
  { county: 'Maryland', amount: 22.8, target: 28 },
  { county: 'Sinoe', amount: 67.3, target: 70 },
];

const METHOD_DATA = [
  { name: 'Bank Transfer', value: 38, color: '#60a5fa', amount: 820.9 },
  { name: 'Orange Money', value: 24, color: '#f59e0b', amount: 27.5 },
  { name: 'MTN Money', value: 18, color: '#fbbf24', amount: 14.0 },
  { name: 'POS / Card', value: 10, color: '#a78bfa', amount: 31.7 },
  { name: 'USSD', value: 5, color: '#34d399', amount: 9.4 },
  { name: 'Cash', value: 3, color: '#fb923c', amount: 8.9 },
  { name: 'Cheque', value: 2, color: '#94a3b8', amount: 67.3 },
];

const RECON_SUMMARY = [
  { label: 'Reconciled', value: 9, color: '#34d399' },
  { label: 'Pending Bank', value: 1, color: '#60a5fa' },
  { label: 'In Review', value: 1, color: '#fbbf24' },
  { label: 'Exception', value: 3, color: '#f87171' },
  { label: 'Unreconciled', value: 1, color: '#fb923c' },
];

// ─── Status Configs ───────────────────────────────────────────────────────────

const PAYMENT_STATUS_CONFIG: Record<PaymentStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  settled:  { label: 'Settled',  color: '#34d399', bg: 'rgba(52,211,153,0.12)',  icon: CheckCircle },
  pending:  { label: 'Pending',  color: '#fbbf24', bg: 'rgba(251,191,36,0.12)',  icon: Clock },
  failed:   { label: 'Failed',   color: '#f87171', bg: 'rgba(248,113,113,0.12)', icon: XCircle },
  reversed: { label: 'Reversed', color: '#fb923c', bg: 'rgba(251,146,60,0.12)',  icon: RotateCcw },
  disputed: { label: 'Disputed', color: '#e879f9', bg: 'rgba(232,121,249,0.12)', icon: AlertTriangle },
  partial:  { label: 'Partial',  color: '#38bdf8', bg: 'rgba(56,189,248,0.12)',  icon: AlertCircle },
  exempt:   { label: 'Exempt',   color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', icon: ShieldCheck },
};

const RECON_STATUS_CONFIG: Record<ReconciliationStatus, { label: string; color: string; bg: string }> = {
  reconciled:   { label: 'Reconciled',   color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  unreconciled: { label: 'Unreconciled', color: '#fb923c', bg: 'rgba(251,146,60,0.12)' },
  in_review:    { label: 'In Review',    color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
  exception:    { label: 'Exception',    color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
  pending_bank: { label: 'Pending Bank', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)' },
};

const METHOD_ICONS: Record<string, React.ElementType> = {
  'Bank Transfer': Building2,
  'Orange Money': Phone,
  'MTN Money': Phone,
  'POS / Card': CreditCard,
  'USSD': Hash,
  'Cash': DollarSign,
  'Cheque': FileText,
  'Online Portal': Layers,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number) => `$${n.toLocaleString()}`;
const fmtM = (n: number) => `$${n.toFixed(1)}M`;

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status, config }: { status: string; config: Record<string, { label: string; color: string; bg: string; icon?: React.ElementType }> }) {
  const cfg = config[status];
  if (!cfg) return null;
  const Icon = (cfg as { icon?: React.ElementType }).icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ color: cfg.color, backgroundColor: cfg.bg, border: `1px solid ${cfg.color}30` }}>
      {Icon && <Icon size={10} />}
      {cfg.label}
    </span>
  );
}

function ReconBadge({ status }: { status: ReconciliationStatus }) {
  const cfg = RECON_STATUS_CONFIG[status];
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ color: cfg.color, backgroundColor: cfg.bg, border: `1px solid ${cfg.color}30` }}>
      {cfg.label}
    </span>
  );
}

// ─── Payment Detail Modal ─────────────────────────────────────────────────────

function PaymentDetailModal({ payment, onClose }: { payment: Payment; onClose: () => void }) {
  const [tab, setTab] = useState<'overview' | 'reconciliation' | 'audit'>('overview');
  const pCfg = PAYMENT_STATUS_CONFIG[payment.status];
  const rCfg = RECON_STATUS_CONFIG[payment.reconciliationStatus];
  const MIcon = METHOD_ICONS[payment.paymentMethod] || CreditCard;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-2xl rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--dark-border)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)' }}>
              <Banknote size={18} style={{ color: '#070e1f' }} />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>{payment.id}</p>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{payment.billId} · {payment.taxpayerName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
            <X size={16} style={{ color: 'var(--dark-muted)' }} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-3" style={{ borderBottom: '1px solid var(--dark-border)' }}>
          {(['overview', 'reconciliation', 'audit'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className="px-3 py-2 text-xs font-semibold capitalize rounded-t-lg transition-colors"
              style={{ color: tab === t ? 'var(--accent)' : 'var(--dark-muted)', borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent' }}>
              {t}
            </button>
          ))}
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {tab === 'overview' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                  <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>Amount</p>
                  <p className="text-xl font-bold" style={{ color: 'var(--accent)' }}>{fmt(payment.amount)}</p>
                  {payment.penaltyIncluded > 0 && <p className="text-xs mt-0.5" style={{ color: '#f87171' }}>+{fmt(payment.penaltyIncluded)} penalty</p>}
                  {payment.discountApplied > 0 && <p className="text-xs mt-0.5" style={{ color: '#34d399' }}>-{fmt(payment.discountApplied)} discount</p>}
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                  <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>Status</p>
                  <StatusBadge status={payment.status} config={PAYMENT_STATUS_CONFIG as Record<string, { label: string; color: string; bg: string; icon?: React.ElementType }>} />
                  <p className="text-xs mt-1.5" style={{ color: 'var(--dark-muted)' }}>Recon: <ReconBadge status={payment.reconciliationStatus} /></p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                {[
                  { label: 'Taxpayer', value: payment.taxpayerName },
                  { label: 'Taxpayer ID', value: payment.taxpayerId },
                  { label: 'Bill ID', value: payment.billId },
                  { label: 'Tax Type', value: payment.taxType },
                  { label: 'County', value: payment.county },
                  { label: 'District', value: payment.district },
                  { label: 'Payment Method', value: payment.paymentMethod },
                  { label: 'Channel', value: payment.channel },
                  { label: 'Reference No.', value: payment.referenceNo },
                  { label: 'Bank Reference', value: payment.bankRef || '—' },
                  { label: 'Payment Date', value: payment.paymentDate },
                  { label: 'Value Date', value: payment.valueDate },
                  { label: 'Collection Officer', value: payment.collectionOfficer },
                  { label: 'Office Location', value: payment.officeLocation },
                  { label: 'Installment', value: payment.installmentNo ? `${payment.installmentNo} of ${payment.totalInstallments}` : 'Full Payment' },
                  { label: 'Notes', value: payment.notes || '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="p-2 rounded" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                    <p style={{ color: 'var(--dark-muted)' }}>{label}</p>
                    <p className="font-semibold mt-0.5" style={{ color: 'var(--dark-text)' }}>{value}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === 'reconciliation' && (
            <div className="space-y-3">
              <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--dark-elevated)', border: `1px solid ${rCfg.color}40` }}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>Reconciliation Status</p>
                  <ReconBadge status={payment.reconciliationStatus} />
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p style={{ color: 'var(--dark-muted)' }}>Reconciled By</p>
                    <p className="font-semibold mt-0.5" style={{ color: 'var(--dark-text)' }}>{payment.reconciledBy || '—'}</p>
                  </div>
                  <div>
                    <p style={{ color: 'var(--dark-muted)' }}>Reconciled Date</p>
                    <p className="font-semibold mt-0.5" style={{ color: 'var(--dark-text)' }}>{payment.reconciledDate || '—'}</p>
                  </div>
                </div>
              </div>
              {payment.notes && (
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: '#fbbf24' }}>Note</p>
                  <p className="text-xs" style={{ color: 'var(--dark-text)' }}>{payment.notes}</p>
                </div>
              )}
              <div className="p-3 rounded-lg text-xs space-y-2" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                <p className="font-semibold" style={{ color: 'var(--dark-text)' }}>Bank Reference Match</p>
                <div className="flex items-center justify-between">
                  <span style={{ color: 'var(--dark-muted)' }}>System Reference</span>
                  <span className="font-mono" style={{ color: 'var(--dark-text)' }}>{payment.referenceNo}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span style={{ color: 'var(--dark-muted)' }}>Bank TXN Ref</span>
                  <span className="font-mono" style={{ color: 'var(--dark-text)' }}>{payment.bankRef || 'Not available'}</span>
                </div>
              </div>
            </div>
          )}

          {tab === 'audit' && (
            <div className="space-y-2">
              {[
                { time: payment.paymentDate + ' 09:14', action: 'Payment Received', by: payment.collectionOfficer, detail: `${fmt(payment.amount)} via ${payment.paymentMethod}` },
                { time: payment.paymentDate + ' 09:15', action: 'System Record Created', by: 'LRA System', detail: `Payment ID ${payment.id} assigned` },
                { time: payment.valueDate + ' 08:00', action: 'Value Date Confirmed', by: 'Treasury', detail: `Funds available ${payment.valueDate}` },
                ...(payment.reconciledDate ? [{ time: payment.reconciledDate + ' 11:30', action: 'Reconciliation Completed', by: payment.reconciledBy, detail: 'Matched against bank statement' }] : []),
                ...(payment.notes ? [{ time: payment.paymentDate + ' 14:00', action: 'Note Added', by: payment.collectionOfficer, detail: payment.notes }] : []),
              ].map((entry, i) => (
                <div key={i} className="flex gap-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                  <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: 'var(--accent)' }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold" style={{ color: 'var(--dark-text)' }}>{entry.action}</p>
                      <p className="text-xs flex-shrink-0" style={{ color: 'var(--dark-muted)' }}>{entry.time}</p>
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{entry.by} · {entry.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3" style={{ borderTop: '1px solid var(--dark-border)' }}>
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-xs font-semibold transition-colors hover:bg-white/10"
            style={{ color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}>
            Close
          </button>
          <button className="px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors"
            style={{ backgroundColor: 'var(--accent)', color: '#070e1f' }}>
            <Printer size={12} /> Print Receipt
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CollectionsClient() {
  const [activeTab, setActiveTab] = useState<'payments' | 'reconciliation' | 'analytics' | 'county' | 'methods'>('payments');
  const [search, setSearch] = useState('');
  const [countyFilter, setCountyFilter] = useState('All Counties');
  const [statusFilter, setStatusFilter] = useState('All');
  const [methodFilter, setMethodFilter] = useState('All');
  const [reconFilter, setReconFilter] = useState('All');
  const [sortField, setSortField] = useState<keyof Payment>('paymentDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSort = (field: keyof Payment) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const filtered = useMemo(() => {
    return PAYMENTS.filter(p => {
      const q = search.toLowerCase();
      const matchSearch = !q || p.taxpayerName.toLowerCase().includes(q) || p.id.toLowerCase().includes(q) || p.billId.toLowerCase().includes(q) || p.taxpayerId.toLowerCase().includes(q) || p.referenceNo.toLowerCase().includes(q);
      const matchCounty = countyFilter === 'All Counties' || p.county === countyFilter;
      const matchStatus = statusFilter === 'All' || p.status === statusFilter;
      const matchMethod = methodFilter === 'All' || p.paymentMethod === methodFilter;
      const matchRecon = reconFilter === 'All' || p.reconciliationStatus === reconFilter;
      return matchSearch && matchCounty && matchStatus && matchMethod && matchRecon;
    }).sort((a, b) => {
      const av = a[sortField] as string | number;
      const bv = b[sortField] as string | number;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [search, countyFilter, statusFilter, methodFilter, reconFilter, sortField, sortDir]);

  const totalCollected = PAYMENTS.filter(p => p.status === 'settled').reduce((s, p) => s + p.amount, 0);
  const totalPending = PAYMENTS.filter(p => p.status === 'pending').reduce((s, p) => s + p.amount, 0);
  const totalFailed = PAYMENTS.filter(p => p.status === 'failed' || p.status === 'reversed').length;
  const totalExceptions = RECONCILIATION_ITEMS.filter(r => r.status === 'exception' || r.status === 'unreconciled').length;
  const reconRate = Math.round((PAYMENTS.filter(p => p.reconciliationStatus === 'reconciled').length / PAYMENTS.length) * 100);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const SortIcon = ({ field }: { field: keyof Payment }) => (
    sortField === field
      ? (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)
      : <ChevronDown size={12} style={{ opacity: 0.3 }} />
  );

  const cardStyle = { backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' };
  const elevatedStyle = { backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' };
  const thStyle: React.CSSProperties = { color: 'var(--dark-muted)', fontSize: '11px', fontWeight: 600, padding: '10px 12px', textAlign: 'left' as const, whiteSpace: 'nowrap' as const, cursor: 'pointer' };
  const tdStyle: React.CSSProperties = { padding: '10px 12px', fontSize: '12px', color: 'var(--dark-text)', verticalAlign: 'middle' };

  const TABS = [
    { id: 'payments', label: 'All Payments', icon: Banknote },
    { id: 'reconciliation', label: 'Reconciliation', icon: ShieldCheck },
    { id: 'analytics', label: 'Analytics', icon: BarChart2 },
    { id: 'county', label: 'By County', icon: MapPin },
    { id: 'methods', label: 'By Method', icon: CreditCard },
  ] as const;

  return (
    <AppLayout>
      <div className="min-h-screen px-4 py-5 lg:px-6 xl:px-8 max-w-screen-2xl mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)' }}>
              <Banknote size={20} style={{ color: '#070e1f' }} />
            </div>
            <div>
              <h1 className="text-xl font-bold" style={{ color: 'var(--dark-text)' }}>LRA Collections Dashboard</h1>
              <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>Track all incoming payments · Reconciliation status · County & method breakdown</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ backgroundColor: 'rgba(52,211,153,0.15)', color: '#34d399', border: '1px solid rgba(52,211,153,0.3)' }}>
              <CheckCircle size={11} /> Live · May 2025
            </div>
            <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors hover:bg-white/10"
              style={{ color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}>
              <Download size={12} /> Export
            </button>
            <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors hover:bg-white/10"
              style={{ color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}>
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          {[
            { label: 'Total Collected (May)', value: fmt(totalCollected), sub: 'Settled payments', icon: Banknote, color: 'var(--accent)', trend: '+12.4%' },
            { label: 'Pending Clearance', value: fmt(totalPending), sub: '1 payment awaiting', icon: Clock, color: '#fbbf24', trend: null },
            { label: 'Reconciliation Rate', value: `${reconRate}%`, sub: `${PAYMENTS.filter(p => p.reconciliationStatus === 'reconciled').length} of ${PAYMENTS.length} matched`, icon: ShieldCheck, color: '#34d399', trend: null },
            { label: 'Exceptions / Disputes', value: `${totalExceptions}`, sub: 'Require resolution', icon: AlertTriangle, color: '#f87171', trend: null },
            { label: 'Failed / Reversed', value: `${totalFailed}`, sub: 'This period', icon: XCircle, color: '#fb923c', trend: null },
          ].map((k) => {
            const KIcon = k.icon;
            return (
              <div key={k.label} className="p-4 rounded-xl" style={cardStyle}>
                <div className="flex items-center justify-between mb-2">
                  <KIcon size={16} style={{ color: k.color }} />
                  {k.trend && (
                    <span className="text-xs font-semibold flex items-center gap-0.5" style={{ color: '#34d399' }}>
                      <ArrowUpRight size={10} />{k.trend}
                    </span>
                  )}
                </div>
                <p className="text-lg font-bold" style={{ color: k.color }}>{k.value}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{k.label}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)', opacity: 0.7 }}>{k.sub}</p>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 overflow-x-auto pb-1">
          {TABS.map(t => {
            const TIcon = t.icon;
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors"
                style={{
                  backgroundColor: activeTab === t.id ? 'var(--accent)' : 'var(--dark-card)',
                  color: activeTab === t.id ? '#070e1f' : 'var(--dark-muted)',
                  border: `1px solid ${activeTab === t.id ? 'var(--accent)' : 'var(--dark-border)'}`,
                }}>
                <TIcon size={13} />{t.label}
              </button>
            );
          })}
        </div>

        {/* ── TAB: All Payments ── */}
        {activeTab === 'payments' && (
          <div className="rounded-xl overflow-hidden" style={cardStyle}>
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 p-3" style={{ borderBottom: '1px solid var(--dark-border)' }}>
              <div className="relative flex-1 min-w-48">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search taxpayer, bill ID, reference…"
                  className="w-full pl-8 pr-3 py-1.5 rounded-lg text-xs outline-none"
                  style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }} />
              </div>
              <select value={countyFilter} onChange={e => setCountyFilter(e.target.value)}
                className="px-2 py-1.5 rounded-lg text-xs outline-none"
                style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }}>
                {COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                className="px-2 py-1.5 rounded-lg text-xs outline-none"
                style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }}>
                <option value="All">All Statuses</option>
                {Object.entries(PAYMENT_STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={methodFilter} onChange={e => setMethodFilter(e.target.value)}
                className="px-2 py-1.5 rounded-lg text-xs outline-none"
                style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }}>
                <option value="All">All Methods</option>
                {['Bank Transfer', 'Orange Money', 'MTN Money', 'POS / Card', 'USSD', 'Cash', 'Cheque', 'Online Portal'].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={reconFilter} onChange={e => setReconFilter(e.target.value)}
                className="px-2 py-1.5 rounded-lg text-xs outline-none"
                style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }}>
                <option value="All">All Recon Status</option>
                {Object.entries(RECON_STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <span className="text-xs ml-auto" style={{ color: 'var(--dark-muted)' }}>{filtered.length} records</span>
            </div>

            {/* Batch actions */}
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-3 px-4 py-2" style={{ backgroundColor: 'rgba(212,160,23,0.1)', borderBottom: '1px solid rgba(212,160,23,0.3)' }}>
                <span className="text-xs font-semibold" style={{ color: 'var(--accent)' }}>{selectedIds.size} selected</span>
                <button className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'rgba(52,211,153,0.15)', color: '#34d399' }}>Mark Reconciled</button>
                <button className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'rgba(96,165,250,0.15)', color: '#60a5fa' }}>Export Selected</button>
                <button onClick={() => setSelectedIds(new Set())} className="text-xs ml-auto" style={{ color: 'var(--dark-muted)' }}>Clear</button>
              </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{ backgroundColor: 'var(--dark-elevated)' }}>
                  <tr>
                    <th style={{ ...thStyle, width: 36 }}>
                      <button onClick={() => setSelectedIds(selectedIds.size === filtered.length ? new Set() : new Set(filtered.map(p => p.id)))}>
                        {selectedIds.size === filtered.length && filtered.length > 0
                          ? <CheckSquare size={13} style={{ color: 'var(--accent)' }} />
                          : <Square size={13} style={{ color: 'var(--dark-muted)' }} />}
                      </button>
                    </th>
                    {[
                      { label: 'Payment ID', field: 'id' },
                      { label: 'Taxpayer', field: 'taxpayerName' },
                      { label: 'Bill ID', field: 'billId' },
                      { label: 'County', field: 'county' },
                      { label: 'Tax Type', field: 'taxType' },
                      { label: 'Amount', field: 'amount' },
                      { label: 'Method', field: 'paymentMethod' },
                      { label: 'Date', field: 'paymentDate' },
                      { label: 'Status', field: 'status' },
                      { label: 'Reconciliation', field: 'reconciliationStatus' },
                    ].map(col => (
                      <th key={col.field} style={thStyle} onClick={() => toggleSort(col.field as keyof Payment)}>
                        <span className="flex items-center gap-1">{col.label}<SortIcon field={col.field as keyof Payment} /></span>
                      </th>
                    ))}
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p, i) => {
                    const MIcon = METHOD_ICONS[p.paymentMethod] || CreditCard;
                    return (
                      <tr key={p.id} style={{ borderTop: '1px solid var(--dark-border)', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                        <td style={tdStyle}>
                          <button onClick={() => toggleSelect(p.id)}>
                            {selectedIds.has(p.id)
                              ? <CheckSquare size={13} style={{ color: 'var(--accent)' }} />
                              : <Square size={13} style={{ color: 'var(--dark-muted)' }} />}
                          </button>
                        </td>
                        <td style={tdStyle}><span className="font-mono text-xs" style={{ color: 'var(--accent)' }}>{p.id}</span></td>
                        <td style={tdStyle}>
                          <p className="font-semibold" style={{ color: 'var(--dark-text)' }}>{p.taxpayerName}</p>
                          <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{p.taxpayerId}</p>
                        </td>
                        <td style={tdStyle}><span className="font-mono text-xs" style={{ color: 'var(--dark-muted)' }}>{p.billId}</span></td>
                        <td style={tdStyle}>
                          <span className="flex items-center gap-1"><MapPin size={11} style={{ color: 'var(--dark-muted)' }} />{p.county}</span>
                        </td>
                        <td style={tdStyle}><span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(96,165,250,0.1)', color: '#60a5fa' }}>{p.taxType}</span></td>
                        <td style={tdStyle}>
                          <p className="font-bold" style={{ color: 'var(--accent)' }}>{fmt(p.amount)}</p>
                          {p.installmentNo && <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Inst. {p.installmentNo}/{p.totalInstallments}</p>}
                        </td>
                        <td style={tdStyle}>
                          <span className="flex items-center gap-1.5"><MIcon size={12} style={{ color: 'var(--dark-muted)' }} />{p.paymentMethod}</span>
                        </td>
                        <td style={tdStyle}>{p.paymentDate}</td>
                        <td style={tdStyle}><StatusBadge status={p.status} config={PAYMENT_STATUS_CONFIG as Record<string, { label: string; color: string; bg: string; icon?: React.ElementType }>} /></td>
                        <td style={tdStyle}><ReconBadge status={p.reconciliationStatus} /></td>
                        <td style={tdStyle}>
                          <button onClick={() => setSelectedPayment(p)}
                            className="flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold transition-colors hover:bg-white/10"
                            style={{ color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.3)' }}>
                            <Eye size={11} /> View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="text-center py-12" style={{ color: 'var(--dark-muted)' }}>
                  <Search size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No payments match your filters</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── TAB: Reconciliation ── */}
        {activeTab === 'reconciliation' && (
          <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              {RECON_SUMMARY.map(r => (
                <div key={r.label} className="p-3 rounded-xl text-center" style={cardStyle}>
                  <p className="text-2xl font-bold" style={{ color: r.color }}>{r.value}</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--dark-muted)' }}>{r.label}</p>
                </div>
              ))}
            </div>

            {/* Reconciliation table */}
            <div className="rounded-xl overflow-hidden" style={cardStyle}>
              <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--dark-border)' }}>
                <p className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>Reconciliation Queue</p>
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(248,113,113,0.15)', color: '#f87171' }}>
                  {RECONCILIATION_ITEMS.filter(r => r.status !== 'reconciled').length} open items
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{ backgroundColor: 'var(--dark-elevated)' }}>
                    <tr>
                      {['Recon ID', 'Payment ID', 'Taxpayer', 'Amount', 'Bank Ref', 'Variance', 'Status', 'Assigned To', 'Flag Reason', 'Created', 'Actions'].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {RECONCILIATION_ITEMS.map((r, i) => (
                      <tr key={r.id} style={{ borderTop: '1px solid var(--dark-border)', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                        <td style={tdStyle}><span className="font-mono text-xs" style={{ color: 'var(--accent)' }}>{r.id}</span></td>
                        <td style={tdStyle}><span className="font-mono text-xs" style={{ color: 'var(--dark-muted)' }}>{r.paymentId}</span></td>
                        <td style={tdStyle}>
                          <p className="font-semibold" style={{ color: 'var(--dark-text)' }}>{r.taxpayerName}</p>
                          <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{r.billId}</p>
                        </td>
                        <td style={tdStyle}><span className="font-bold" style={{ color: 'var(--accent)' }}>{fmt(r.amount)}</span></td>
                        <td style={tdStyle}><span className="font-mono text-xs" style={{ color: 'var(--dark-muted)' }}>{r.bankStatement}</span></td>
                        <td style={tdStyle}>
                          <span className="font-bold" style={{ color: r.variance === 0 ? '#34d399' : r.variance < 0 ? '#f87171' : '#fbbf24' }}>
                            {r.variance === 0 ? '—' : (r.variance > 0 ? '+' : '') + fmt(Math.abs(r.variance))}
                          </span>
                        </td>
                        <td style={tdStyle}><ReconBadge status={r.status} /></td>
                        <td style={tdStyle}>{r.assignedTo}</td>
                        <td style={tdStyle}><span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{r.flagReason}</span></td>
                        <td style={tdStyle}>{r.createdDate}</td>
                        <td style={tdStyle}>
                          <div className="flex items-center gap-1">
                            <button className="px-2 py-0.5 rounded text-xs font-semibold transition-colors"
                              style={{ backgroundColor: 'rgba(52,211,153,0.15)', color: '#34d399' }}>Resolve</button>
                            <button className="px-2 py-0.5 rounded text-xs font-semibold transition-colors"
                              style={{ backgroundColor: 'rgba(96,165,250,0.15)', color: '#60a5fa' }}>Review</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: Analytics ── */}
        {activeTab === 'analytics' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Collections Trend */}
              <div className="p-4 rounded-xl" style={cardStyle}>
                <p className="text-sm font-bold mb-1" style={{ color: 'var(--dark-text)' }}>Monthly Collections vs Target</p>
                <p className="text-xs mb-4" style={{ color: 'var(--dark-muted)' }}>USD Millions · Dec 2024 – May 2025</p>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={TREND_DATA}>
                    <defs>
                      <linearGradient id="collGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#d4a017" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#d4a017" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ backgroundColor: '#162444', border: '1px solid #2a4070', borderRadius: 8, fontSize: 12 }} />
                    <Area type="monotone" dataKey="collected" stroke="#d4a017" strokeWidth={2} fill="url(#collGrad)" name="Collected ($M)" />
                    <Line type="monotone" dataKey="target" stroke="#60a5fa" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Target ($M)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Reconciliation Breakdown */}
              <div className="p-4 rounded-xl" style={cardStyle}>
                <p className="text-sm font-bold mb-1" style={{ color: 'var(--dark-text)' }}>Reconciliation Status Breakdown</p>
                <p className="text-xs mb-4" style={{ color: 'var(--dark-muted)' }}>Current period · {PAYMENTS.length} total payments</p>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={RECON_SUMMARY} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                      {RECON_SUMMARY.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#162444', border: '1px solid #2a4070', borderRadius: 8, fontSize: 12 }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Payment Status Distribution */}
            <div className="p-4 rounded-xl" style={cardStyle}>
              <p className="text-sm font-bold mb-1" style={{ color: 'var(--dark-text)' }}>Payment Status Distribution</p>
              <p className="text-xs mb-4" style={{ color: 'var(--dark-muted)' }}>Count by status · Current period</p>
              <div className="grid grid-cols-3 sm:grid-cols-7 gap-3">
                {Object.entries(PAYMENT_STATUS_CONFIG).map(([key, cfg]) => {
                  const count = PAYMENTS.filter(p => p.status === key).length;
                  const pct = Math.round((count / PAYMENTS.length) * 100);
                  return (
                    <div key={key} className="p-3 rounded-lg text-center" style={elevatedStyle}>
                      <p className="text-xl font-bold" style={{ color: cfg.color }}>{count}</p>
                      <p className="text-xs mt-0.5" style={{ color: cfg.color }}>{cfg.label}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{pct}%</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: By County ── */}
        {activeTab === 'county' && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl" style={cardStyle}>
              <p className="text-sm font-bold mb-1" style={{ color: 'var(--dark-text)' }}>Collections by County</p>
              <p className="text-xs mb-4" style={{ color: 'var(--dark-muted)' }}>USD Millions · Collected vs Target</p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={COUNTY_DATA} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                  <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="county" type="category" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} width={100} />
                  <Tooltip contentStyle={{ backgroundColor: '#162444', border: '1px solid #2a4070', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="amount" fill="#d4a017" radius={[0, 4, 4, 0]} name="Collected ($M)" />
                  <Bar dataKey="target" fill="rgba(96,165,250,0.3)" radius={[0, 4, 4, 0]} name="Target ($M)" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* County table */}
            <div className="rounded-xl overflow-hidden" style={cardStyle}>
              <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--dark-border)' }}>
                <p className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>County Payment Summary</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{ backgroundColor: 'var(--dark-elevated)' }}>
                    <tr>
                      {['County', 'Payments', 'Total Collected', 'Target', 'Achievement', 'Settled', 'Pending', 'Exceptions'].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {COUNTY_DATA.map((c, i) => {
                      const countyPayments = PAYMENTS.filter(p => p.county === c.county);
                      const settled = countyPayments.filter(p => p.status === 'settled').length;
                      const pending = countyPayments.filter(p => p.status === 'pending').length;
                      const exceptions = countyPayments.filter(p => p.status === 'failed' || p.status === 'disputed' || p.status === 'reversed').length;
                      const pct = Math.round((c.amount / c.target) * 100);
                      return (
                        <tr key={c.county} style={{ borderTop: '1px solid var(--dark-border)', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                          <td style={tdStyle}><span className="font-semibold" style={{ color: 'var(--dark-text)' }}>{c.county}</span></td>
                          <td style={tdStyle}>{countyPayments.length}</td>
                          <td style={tdStyle}><span className="font-bold" style={{ color: 'var(--accent)' }}>{fmtM(c.amount)}</span></td>
                          <td style={tdStyle}>{fmtM(c.target)}</td>
                          <td style={tdStyle}>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: 'var(--dark-elevated)', minWidth: 60 }}>
                                <div className="h-1.5 rounded-full" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: pct >= 100 ? '#34d399' : pct >= 80 ? '#fbbf24' : '#f87171' }} />
                              </div>
                              <span className="text-xs font-semibold" style={{ color: pct >= 100 ? '#34d399' : pct >= 80 ? '#fbbf24' : '#f87171' }}>{pct}%</span>
                            </div>
                          </td>
                          <td style={tdStyle}><span style={{ color: '#34d399' }}>{settled}</span></td>
                          <td style={tdStyle}><span style={{ color: '#fbbf24' }}>{pending}</span></td>
                          <td style={tdStyle}><span style={{ color: '#f87171' }}>{exceptions}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: By Method ── */}
        {activeTab === 'methods' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl" style={cardStyle}>
                <p className="text-sm font-bold mb-1" style={{ color: 'var(--dark-text)' }}>Payment Method Distribution</p>
                <p className="text-xs mb-4" style={{ color: 'var(--dark-muted)' }}>By transaction count %</p>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={METHOD_DATA} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="value">
                      {METHOD_DATA.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#162444', border: '1px solid #2a4070', borderRadius: 8, fontSize: 12 }} formatter={(v) => [`${v}%`, 'Share']} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="p-4 rounded-xl" style={cardStyle}>
                <p className="text-sm font-bold mb-1" style={{ color: 'var(--dark-text)' }}>Volume by Payment Method</p>
                <p className="text-xs mb-4" style={{ color: 'var(--dark-muted)' }}>USD Millions collected per channel</p>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={METHOD_DATA}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ backgroundColor: '#162444', border: '1px solid #2a4070', borderRadius: 8, fontSize: 12 }} formatter={(v) => [`$${v}M`, 'Collected']} />
                    <Bar dataKey="amount" radius={[4, 4, 0, 0]} name="Collected ($M)">
                      {METHOD_DATA.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Method breakdown table */}
            <div className="rounded-xl overflow-hidden" style={cardStyle}>
              <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--dark-border)' }}>
                <p className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>Payment Method Breakdown</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{ backgroundColor: 'var(--dark-elevated)' }}>
                    <tr>
                      {['Method', 'Transactions', 'Share %', 'Volume (USD)', 'Avg. Transaction', 'Settled', 'Failed', 'Recon Rate'].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {METHOD_DATA.map((m, i) => {
                      const methodPayments = PAYMENTS.filter(p => p.paymentMethod === m.name);
                      const settled = methodPayments.filter(p => p.status === 'settled').length;
                      const failed = methodPayments.filter(p => p.status === 'failed' || p.status === 'reversed').length;
                      const reconRate = methodPayments.length > 0 ? Math.round((methodPayments.filter(p => p.reconciliationStatus === 'reconciled').length / methodPayments.length) * 100) : 0;
                      const avg = methodPayments.length > 0 ? Math.round(m.amount * 1000000 / methodPayments.length) : 0;
                      const MIcon = METHOD_ICONS[m.name] || CreditCard;
                      return (
                        <tr key={m.name} style={{ borderTop: '1px solid var(--dark-border)', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                          <td style={tdStyle}>
                            <span className="flex items-center gap-2">
                              <MIcon size={14} style={{ color: m.color }} />
                              <span className="font-semibold" style={{ color: 'var(--dark-text)' }}>{m.name}</span>
                            </span>
                          </td>
                          <td style={tdStyle}>{methodPayments.length}</td>
                          <td style={tdStyle}>
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 rounded-full" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                                <div className="h-1.5 rounded-full" style={{ width: `${m.value}%`, backgroundColor: m.color }} />
                              </div>
                              <span>{m.value}%</span>
                            </div>
                          </td>
                          <td style={tdStyle}><span className="font-bold" style={{ color: m.color }}>{fmtM(m.amount)}</span></td>
                          <td style={tdStyle}>{methodPayments.length > 0 ? fmt(avg) : '—'}</td>
                          <td style={tdStyle}><span style={{ color: '#34d399' }}>{settled}</span></td>
                          <td style={tdStyle}><span style={{ color: '#f87171' }}>{failed}</span></td>
                          <td style={tdStyle}>
                            <span className="font-semibold" style={{ color: reconRate >= 80 ? '#34d399' : reconRate >= 50 ? '#fbbf24' : '#f87171' }}>
                              {methodPayments.length > 0 ? `${reconRate}%` : '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Payment Detail Modal */}
      {selectedPayment && <PaymentDetailModal payment={selectedPayment} onClose={() => setSelectedPayment(null)} />}
    </AppLayout>
  );
}
